import React, { useEffect, useState } from "react";
import { supabase } from "../../supabase-client";
import CotizacionModal from "../../components/modal/CotizacionModalADM";
import { ESTADOS_COTIZACION, getEstadoConfig } from "../../utils/estados"; // 👈 Import from utils
import { Eye } from "lucide-react";

export default function Miscotizaciones() {
   const [cotizaciones, setCotizacions] = useState([]);
   const [loading, setLoading] = useState(true);
   const [selectedCotizacion, setSelectedCotizacion] = useState(null);
   const [filterEstado, setFilterEstado] = useState("todos");

   // 👈 Use imported estados instead of local definition
   const estados = ESTADOS_COTIZACION;

   // Fetch cotizaciones from database
   const fetchCotizacions = async () => {
      setLoading(true);
      try {
         const { data, error } = await supabase
            .from("cotizacion")
            .select(`
               *,
               usuarios (
                  id,
                  nombre,
                  email,
                  documento,
                  celular
               )
            `)
            .order("created_at", { ascending: false });

         if (error) {
            console.error("Error fetching cotizaciones:", error);
            alert("Error al cargar las cotizaciones");
            return;
         }

         setCotizacions(data || []);
         console.log("data: ", data);
      } catch (err) {
         console.error("Error:", err);
         alert("Error al conectar con la base de datos");
      }
      setLoading(false);
   };

   // Filter cotizaciones by estado
   const filteredCotizacions =
      filterEstado === "todos"
         ? cotizaciones
         : cotizaciones.filter((cotizacion) => cotizacion.estado === filterEstado);

   // Format date
   const formatDate = (dateString) => {
      return new Date(dateString).toLocaleString("es-AR", {
         year: "numeric",
         month: "2-digit",
         day: "2-digit",
         hour: "2-digit",
         minute: "2-digit",
      });
   };

   const verCotizacion = (cotizacion) => {
      setSelectedCotizacion(cotizacion);
   };

   const cerrarModal = () => {
      setSelectedCotizacion(null);
      fetchCotizacions();
   };

   useEffect(() => {
      fetchCotizacions();
   }, []);

   if (loading) {
      return (
         <div className="p-6 text-center">
            <h1 className="text-3xl font-bold mb-4">Cotizaciones</h1>
            <div className="flex items-center justify-center">
               <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
               <span className="ml-2">Cargando cotizaciones...</span>
            </div>
         </div>
      );
   }

   return (
      <div className="p-6 max-w-7xl mx-auto">
         <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold">Gestión de Cotizaciones</h1>
         </div>

         {/* Statistics */}
         <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg text-center">
               <div className="text-2xl font-bold text-gray-700">
                  {cotizaciones.length}
               </div>
               <div className="text-sm text-gray-600">Total</div>
            </div>
            {Object.entries(estados).slice(0, 5).map(([key, config]) => {
               const count = cotizaciones.filter((p) => p.estado === key).length;
               return (
                  <div key={key} className="bg-gray-50 p-4 rounded-lg text-center">
                     <div className="text-2xl font-bold text-gray-700">{count}</div>
                     <div className="text-sm text-gray-600">{config.label}</div>
                  </div>
               );
            })}
         </div>

         {/* Filters */}
         <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
               Filtrar por estado:
            </label>
            <select
               value={filterEstado}
               onChange={(e) => setFilterEstado(e.target.value)}
               className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
               <option value="todos">Todos los estados</option>
               {Object.entries(estados).map(([key, config]) => (
                  <option key={key} value={key}>
                     {config.label}
                  </option>
               ))}
            </select>
         </div>

         {/* Cotizaciones Table */}
         {filteredCotizacions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
               {filterEstado === "todos"
                  ? "No hay cotizaciones registradas"
                  : `No hay cotizaciones con estado: ${estados[filterEstado]?.label}`}
            </div>
         ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
               <table className="w-full">
                  <thead className="bg-gray-50">
                     <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                           ID
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                           Fecha Cotización
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                           Usuario
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                           Título
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                           Precio
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                           Estado
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                           Acciones
                        </th>
                     </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                     {filteredCotizacions.map((cotizacion) => {
                        // 👈 Use helper function instead of manual lookup
                        const estadoConfig = getEstadoConfig(cotizacion.estado);
                        const IconComponent = estadoConfig.icon;

                        return (
                           <tr key={cotizacion.id} className="hover:bg-gray-50">
                              <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                 #{cotizacion.id}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                 {formatDate(cotizacion.created_at)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                 {cotizacion.usuarios?.nombre || 'N/A'}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                 <div className="text-sm font-medium text-gray-900">
                                    {cotizacion.titulo || "N/A"}
                                 </div>
                                 {cotizacion.detalle && (
                                    <div className="text-sm text-gray-500 truncate max-w-xs">
                                       {cotizacion.detalle}
                                    </div>
                                 )}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                                 {cotizacion.precio > 0 ? (
                                    `U$S ${Number(cotizacion.precio).toLocaleString()}`
                                 ) : (
                                    <span className="text-gray-400">Pendiente</span>
                                 )}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                 <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${estadoConfig.color}`}
                                 >
                                    <IconComponent className="w-3 h-3 mr-1" />
                                    {estadoConfig.label}
                                 </span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                 <div className="flex space-x-2">
                                    <button
                                       onClick={() => verCotizacion(cotizacion)}
                                       className="text-blue-600 hover:text-blue-900 transition-colors p-1 rounded hover:bg-blue-50"
                                       title="Ver detalles"
                                    >
                                       <Eye className="w-4 h-4" />
                                    </button>
                                 </div>
                              </td>
                           </tr>
                        );
                     })}
                  </tbody>
               </table>
            </div>
         )}

         {selectedCotizacion && (
            <CotizacionModal
               cotizacion={selectedCotizacion}
               onClose={cerrarModal}
            />
         )}
      </div>
   );
}